<template>
  <div
    class="flex flex-col items-stretch gap-4"
    :class="layout === 'pair' ? 'lg:flex-row lg:items-center lg:gap-6' : ''"
  >
    <div class="min-w-0 flex-1">
      <div class="app-card-glass shadow-sm rounded-2xl p-4">
        <div class="flex items-center gap-3">
          <AppAvatar
            :label="contactInitials"
            size="normal"
            class="!h-12 !w-12 !rounded-full flex-shrink-0 font-bold text-sm! shadow-sm shadow-[var(--p-primary-color)] dark:bg-[var(--p-primary-color)]/20!"
          />
          <div class="min-w-0 flex-1">
            <p
              v-if="contactLabel"
              class="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--p-primary-color)]/80 dark:text-[var(--p-primary-300)]"
            >
              {{ contactLabel }}
            </p>
            <p
              class="truncate text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
            >
              {{ contactName }}
            </p>
            <p
              class="truncate text-xs text-[var(--p-surface-700)] dark:text-[var(--p-surface-100)]"
            >
              {{ contactPhone }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <template v-if="layout === 'pair'">
      <div class="flex items-center justify-center">
        <component
          :is="transferStatusIcon"
          width="35"
          height="35"
          :class="
            props.isAccepted
              ? 'text-[var(--p-primary-color)] dark:text-[var(--p-surface-100)]'
              : 'text-red-600'
          "
        />
      </div>

      <div class="min-w-0 flex-1">
        <div class="app-card-glass shadow-sm rounded-2xl p-4 rounded-2xl p-4">
          <div class="flex items-center gap-3">
            <AppAvatar
              v-if="transferType === 'user'"
              :label="destinationInitials"
              size="normal"
              class="!h-12 !w-12 !rounded-full flex-shrink-0 font-bold text-sm! shadow-sm shadow-[var(--p-primary-color)] dark:bg-[var(--p-primary-color)]/20!"
            />

            <div
              v-else
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--p-primary-color)]/12 text-[var(--p-primary-color)]"
            >
              <component :is="destinationIcon" width="22" height="22" />
            </div>

            <div class="min-w-0 flex-1">
              <p
                v-if="destinationLabel"
                class="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--p-primary-color)]/80 dark:text-[var(--p-primary-300)]"
              >
                {{ destinationLabel }}
              </p>
              <p
                class="truncate text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
              >
                {{ destinationName }}
              </p>
              <p
                v-if="destinationSubtitle"
                class="truncate text-xs text-[var(--p-surface-700)] dark:text-[var(--p-surface-100)]"
              >
                {{ destinationSubtitle }}
              </p>
              <!-- <p
                v-if="destinationMeta"
                class="truncate text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-500)]"
              >
                {{ destinationMeta }}
              </p> -->
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import IconTransfer from '@/assets/svg/transfer_icon.svg?component'
import IconNotTransfer from '@/assets/svg/not_transfer_icon.svg?component'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import IconBot from '@/assets/svg/bot_icon.svg?component'

interface Props {
  contactName: string
  contactPhone: string
  transferType?: 'campaign' | 'user' | 'bot'
  isAccepted?: boolean
  destinationName?: string
  destinationSubtitle?: string | null
  destinationMeta?: string | null
  contactLabel?: string | null
  destinationLabel?: string | null
  layout?: 'pair' | 'single'
}

const props = withDefaults(defineProps<Props>(), {
  transferType: 'user',
  isAccepted: true,
  destinationName: '',
  destinationSubtitle: null,
  destinationMeta: null,
  contactLabel: null,
  destinationLabel: null,
  layout: 'pair',
})

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

const contactInitials = computed(() => getInitials(props.contactName || props.contactPhone))
const destinationInitials = computed(() => getInitials(props.destinationName))
const transferStatusIcon = computed(() => (props.isAccepted ? IconTransfer : IconNotTransfer))

const destinationIcon = computed(() => {
  if (props.transferType === 'campaign') return IconCampaign
  return IconBot
})
</script>
